import { useStore } from "@/stores/useStore";
import { useEffect, useMemo, useState } from "react";
import { Icon } from "@/components/Common/Icon";
import { IconNames } from "@/types/v2";
import {
  DateRangeSelector,
  DateRange,
} from "@/components/Metrics/DateRangeSelector";
import { useOrderCounts } from "@/hooks/queries/useOrderCounts";
import { useCardStatusMetrics } from "@/hooks/queries/useCardStatusMetrics";
import { BarChart } from "@/components/Base/BarChart";
import { ContainerTimeChart } from "@/components/Metrics/ContainerTimeChart";
import { ProfitabilityChart } from "@/components/Metrics/ProfitabilityChart";
import { Tooltip } from "@/components/Common/Tooltip";
import { useMetricsDownload } from "@/hooks/useMetricsDownload";
import { useHeaderContext } from "@/hooks/useHeaderContext";
import { MetricsHeader } from "@/components/Page/Header/MetricsHeader";

const MetricsPage = () => {
  const cards = useStore((s) => s.cards);
  const shop = useStore((s) => s.shop);
  const shopId = useMemo(() => shop?.id, [shop]);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>(() => {
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return { label: "Last 7 Days", start: sevenDaysAgo, end: now };
  });
  const { orderCounts, isLoading: isLoadingMetrics } = useOrderCounts({
    start: selectedDateRange.start,
    end: selectedDateRange.end,
  });
  const { statusBreakdown, completionTimeStats } = useCardStatusMetrics({
    start: selectedDateRange.start,
    end: selectedDateRange.end,
  });
  const { downloadMetrics, isDownloading, error } = useMetricsDownload(
    selectedDateRange,
    shop?.name
  );
  const { setHeaderContent } = useHeaderContext();

  useEffect(() => {
    setHeaderContent(shopId ? <MetricsHeader shopId={shopId} /> : null);
    return () => setHeaderContent(null);
  }, [setHeaderContent, shopId]);

  // Handle date range change with loading state
  const handleDateRangeChange = (newRange: DateRange) => {
    setSelectedDateRange(newRange);
  };

  // Calculate metrics using cloud function data with fallback to client data
  const metrics = useMemo(() => {
    // Use cloud function data when available, otherwise fallback to client-side calculation
    const totalCards = statusBreakdown?.totalCards ?? cards.length;
    const totalClosed = statusBreakdown?.closedCards ?? 0;
    const totalOpen = statusBreakdown?.openCards ?? totalCards - totalClosed;

    // Use completion time from cloud function (user story 3.12 implementation)
    const avgCompletionTime = completionTimeStats?.average ?? 0;

    const dateRangeCreated = orderCounts?.current.created ?? 0;
    const dateRangeCompleted = orderCounts?.current.completed ?? 0;

    const detailedBreakdown = {
      currentPeriodCreated: orderCounts?.current.created ?? 0,
      currentPeriodClosed: orderCounts?.current.completed ?? 0,
      previousPeriodCreated: orderCounts?.previous.created ?? 0,
      previousPeriodClosed: orderCounts?.previous.completed ?? 0,
      rolloverCards: orderCounts?.rollover.count ?? 0,
    };

    return {
      totalCards,
      totalClosed,
      totalOpen,
      avgCompletionTime,
      dateRangeCreated,
      dateRangeCompleted,
      rolloverCount: orderCounts?.rollover.count ?? 0,
      rolloverRate: orderCounts?.rollover.rate ?? 0,
      detailedBreakdown,
    };
  }, [statusBreakdown, completionTimeStats, orderCounts, cards.length]);

  // Prepare 12-month stacked data (open and closed ROs)
  const twelveMonthStackedData = useMemo(() => {
    return orderCounts?.months ?? [];
  }, [orderCounts]);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Metrics</h1>
          <p className="mt-1 text-gray-400">
            View repair order insights and performance metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={downloadMetrics}
            disabled={isDownloading}
            className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isDownloading ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent"></div>
                Downloading...
              </>
            ) : (
              <>
                <Icon name={IconNames.download} size={16} />
                Download
              </>
            )}
          </button>
          {error && (
            <div className="mt-1 text-sm text-red-600">Error: {error}</div>
          )}
          <DateRangeSelector
            selectedRange={selectedDateRange}
            onRangeChange={handleDateRangeChange}
            isLoading={isLoadingMetrics}
          />
        </div>
      </div>

      {/* Cards Currently on the Board */}
      <div className="mb-6 grid grid-cols-1 gap-6">
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Cards on the Board
              </p>
              <p className="text-2xl font-bold text-gray-900">{cards.length}</p>
            </div>
            <div className="rounded-lg bg-blue-100 p-2">
              <Icon name={IconNames.card} size={24} className="text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-gray-500">Currently Active</span>
          </div>
        </div>
      </div>

      {/* This Period Metrics */}
      <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-3">
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Created (This {selectedDateRange.label})
              </p>
              {isLoadingMetrics ? (
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-gray-500">Loading...</span>
                </div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">
                  {metrics.dateRangeCreated}
                </p>
              )}
            </div>
            <div className="rounded-lg bg-blue-100 p-2">
              <Icon name={IconNames.add} size={24} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Completed (This {selectedDateRange.label})
              </p>
              {isLoadingMetrics ? (
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-gray-500">Loading...</span>
                </div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">
                  {metrics.dateRangeCompleted}
                </p>
              )}
            </div>
            <div className="bg-green-100 rounded-lg p-2">
              <Icon
                name={IconNames.check}
                size={24}
                className="text-green-600"
              />
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Open ROs (This {selectedDateRange.label})
              </p>
              {isLoadingMetrics ? (
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-gray-500">Loading...</span>
                </div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">
                  {metrics.dateRangeCreated - metrics.dateRangeCompleted}
                </p>
              )}
            </div>
            <div className="rounded-lg bg-yellow-100 p-2">
              <Icon
                name={IconNames.clock}
                size={24}
                className="text-yellow-600"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Last Period Metrics */}
      <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-3">
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Created (Last {selectedDateRange.label})
              </p>
              {isLoadingMetrics ? (
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-gray-500">Loading...</span>
                </div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">
                  {metrics.detailedBreakdown?.previousPeriodCreated || 0}
                </p>
              )}
            </div>
            <div className="rounded-lg bg-blue-100 p-2">
              <Icon name={IconNames.add} size={24} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Completed (Last {selectedDateRange.label})
              </p>
              {isLoadingMetrics ? (
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-gray-500">Loading...</span>
                </div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">
                  {metrics.detailedBreakdown?.previousPeriodClosed || 0}
                </p>
              )}
            </div>
            <div className="bg-green-100 rounded-lg p-2">
              <Icon
                name={IconNames.check}
                size={24}
                className="text-green-600"
              />
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Open ROs (Last {selectedDateRange.label})
              </p>
              {isLoadingMetrics ? (
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-gray-500">Loading...</span>
                </div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">
                  {(metrics.detailedBreakdown?.previousPeriodCreated || 0) -
                    (metrics.detailedBreakdown?.previousPeriodClosed || 0)}
                </p>
              )}
            </div>
            <div className="rounded-lg bg-yellow-100 p-2">
              <Icon
                name={IconNames.clock}
                size={24}
                className="text-yellow-600"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Rollover Rate and Avg Completion Time */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Avg Completion Time
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {metrics.avgCompletionTime}h
              </p>
            </div>
            <div className="rounded-lg bg-orange-100 p-2">
              <Icon
                name={IconNames.clock}
                size={24}
                className="text-orange-600"
              />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-gray-500">
              This {selectedDateRange.label}
            </span>
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow md:row-span-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="flex items-center gap-1 text-sm font-medium text-gray-600">
                Rollover Rate
                <Tooltip message="Percentage of repair orders from the previous period that are still open in the current period">
                  ⓘ
                </Tooltip>
              </p>
              {isLoadingMetrics ? (
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-gray-500">Loading...</span>
                </div>
              ) : (
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {metrics.rolloverRate}%
                  </p>
                  <div className="mt-1 flex items-center gap-1">
                    <Icon
                      name={IconNames.sync}
                      size={14}
                      className="text-purple-500"
                    />
                    <span className="text-sm font-medium text-purple-600">
                      {metrics.rolloverCount} rollovers
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="rounded-lg bg-purple-100 p-2">
              <Icon
                name={IconNames.metrics}
                size={24}
                className="text-purple-600"
              />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-gray-500">{selectedDateRange.label}</span>
          </div>
        </div>
      </div>
      {/* Additional Metrics */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg bg-white p-6 shadow">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">
            Status Distribution
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Open Cards</span>
              <span className="text-sm font-medium text-gray-900">
                {metrics.totalOpen}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Closed Cards</span>
              <span className="text-sm font-medium text-gray-900">
                {metrics.totalClosed}
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-gray-200">
              <div
                className="h-2 rounded-full bg-success"
                style={{
                  width: `${(metrics.totalClosed / metrics.totalCards) * 100}%`,
                }}
              ></div>
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">
            Performance Summary
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Average Completion Time
              </span>
              <span className="text-sm font-medium text-gray-900">
                {metrics.avgCompletionTime} hours
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1 text-sm text-gray-600">
                {selectedDateRange.label} Rollover Rate
                <span
                  className="text-xs text-gray-400"
                  title="Percentage of repair orders from the previous period that are still open in the current period"
                >
                  ⓘ
                </span>
              </span>
              <span className="text-sm font-medium text-gray-900">
                {metrics.rolloverRate}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Total Cards Processed
              </span>
              <span className="text-sm font-medium text-gray-900">
                {metrics.totalCards}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Chart Section */}
      <div className="rounded-lg bg-white p-6 shadow">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">
          Open vs Closed ROs per Month (Last 12 Months)
        </h3>
        <div className="space-y-4">
          {isLoadingMetrics ? (
            <div className="flex h-64 items-center justify-center">
              <Icon
                name={IconNames.loader}
                size={48}
                className="animate-spin text-blue-500"
              />
              <span className="ml-2 text-gray-600">
                Loading monthly data...
              </span>
            </div>
          ) : (
            <>
              {/* Stacked BarChart */}
              <BarChart
                data={twelveMonthStackedData.map((bucket) => ({
                  month: bucket.month,
                  open: bucket.open,
                  closed: bucket.closed,
                }))}
                index="month"
                categories={["open", "closed"]}
                colors={["hsl(var(--warning))", "hsl(var(--success))"]}
                yAxisWidth={48}
                barSize={80}
                stacked={true}
                className="h-[300px]"
              />
            </>
          )}
        </div>
      </div>

      {/* Container Time Analysis Chart */}
      <ContainerTimeChart selectedDateRange={selectedDateRange} />

      {/* Profitability Chart - Only for Tekmetric shops */}
      <ProfitabilityChart />
    </div>
  );
};

export default MetricsPage;
